import React from 'react';
import CoachHeader from './CoachHeader';
import GameHistorySidebar from './GameHistorySidebar';
import KpiTilesRow from './KpiTilesRow';
import DashboardCenterPanel from './DashboardCenterPanel';
import ActiveBlockCard from './ActiveBlockCard';
import RankProgressBar from '../shell/RankProgressBar';
import DailyMissionCard from '../shell/DailyMissionCard';
import ApexScoreCompact from '../shell/ApexScoreCompact';
import SeasonObjectiveCard from '../shell/SeasonObjectiveCard';
import ApexCirclesRail from './ApexCirclesRail';
import LeftRail from './LeftRail';

/**
 * APEX v7.7.0 — Dashboard refondu avec hiérarchie d'objectifs.
 *
 * Layout (top-down) :
 *   - Coach Header
 *   - Rank Progress Bar
 *   - APEX Score Compact (v7.7.0)
 *   - Daily Mission Card (v7.7.0)
 *   - KPIs row
 *   - Center Panel (LP curve + bloc actif)
 *   - Game History
 *   - Champions + Insights
 */

export default function DashboardV7({
  games,
  miniBlocks,
  rankSnapshots,
  lpDeltasByGame,
  userSettings,
  version,
  importTriggers,
  onAction,
  lpCurveSlot,
  activeBlockSlot,
  // V7.7.0
  dailyMission,
  onSwapMission,
  onSkipMission,
  allGames, // games non filtrées par activeFilter, pour calcul APEX Score
  seasonObjective,
  onSaveSeasonObjective,
  activeFilter,
  onSetFilter
}) {
  const safeGames = Array.isArray(games) ? games : [];
  const safeMiniBlocks = Array.isArray(miniBlocks) ? miniBlocks : [];
  const safeRankSnapshots = Array.isArray(rankSnapshots) ? rankSnapshots : [];
  const safeImportTriggers = Array.isArray(importTriggers) ? importTriggers : [];
  const safeLpDeltas = (lpDeltasByGame && typeof lpDeltasByGame === 'object') ? lpDeltasByGame : {};

  const coachState = {
    games: safeGames,
    miniBlocks: safeMiniBlocks,
    rankSnapshots: safeRankSnapshots,
    importTriggers: safeImportTriggers,
    userSettings
  };

  const handleAction = (action, payload) => onAction?.(action, payload);

  // v7.8.4 : les cutoffs apex + rails sont gérés au niveau global (App → GlobalRails)

  return (
    <div className="space-y-0">
      <CoachHeader state={coachState} onAction={handleAction} />

      {/* v7.8.4 : les rails (gauche + droite) sont désormais gérés au niveau
          global de l'app (GlobalRails) pour être visibles sur tous les onglets. */}

      <div className="max-w-[1600px] mx-auto px-4 md:px-6 py-5 md:py-6 space-y-5">

        {/* v7.8.1 : Ranking / APEX Score / Objectif saison sont désormais
            affichés dans le rail circulaire à droite (ApexCirclesRail).
            Les anciennes cartes/barres ont été retirées pour éviter le doublon. */}

        {/* v7.8.8 : carte Mission retirée — la mission du jour est désormais
            dans le rond "Mission" du rail gauche (avec Changer/Passer). */}

        {/* Row 1 : KPIs 4 tuiles horizontales */}
        <KpiTilesRow
          games={safeGames}
          rankSnapshots={safeRankSnapshots}
          lpDeltasByGame={safeLpDeltas}
          userSettings={userSettings}
        />

        {/* Row 2 : Split LP+Bloc / Historique */}
        <div className="grid gap-5 grid-cols-1 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)]">
          <div className="space-y-5">
            <DashboardCenterPanel
              rankSnapshots={safeRankSnapshots}
              activeBlockSlot={
                activeBlockSlot ?? (
                  <ActiveBlockCard
                    miniBlocks={safeMiniBlocks}
                    games={safeGames}
                    onViewBlock={() => handleAction('view_block')}
                    onCreateBlock={() => handleAction('create_block')}
                  />
                )
              }
              games={safeGames}
              version={version}
              userSettings={userSettings}
              onSuggestionClick={(actionPayload) => {
                if (actionPayload?.type) handleAction(actionPayload.type, actionPayload);
              }}
              onSeeAll={() => handleAction('see_all_champions')}
            />
            {lpCurveSlot}
          </div>

          <div>
            <GameHistorySidebar
              games={safeGames}
              version={version}
              lpDeltasByGame={safeLpDeltas}
              onImportClick={() => handleAction('open_import')}
              onAnalyzeGame={(game) => handleAction('view_analysis', { game })}
              defaultLimit={15}
              miniBlocks={safeMiniBlocks}
              userSettings={userSettings}
            />
          </div>
        </div>

      </div>
    </div>
  );
}
